import React from 'react';

/**
 * Premium SVG visuals — generated entirely from code / text.
 * No stock photos, no human imagery. Deep navy + royal blue + gold palette.
 * Each visual is composable, responsive, and brand-aligned.
 */

const COLORS = {
  navyDeep: '#061328',
  navy: '#0a1b35',
  navyMid: '#122a52',
  blue: '#1e4380',
  blueSoft: '#2d6bb8',
  gold: '#e8b863',
  goldLight: '#f4d18a',
  goldDeep: '#b8842a',
};

function Defs() {
  return (
    <defs>
      <radialGradient id="bgGrad" cx="50%" cy="50%" r="70%">
        <stop offset="0%" stopColor={COLORS.blue} stopOpacity="0.55" />
        <stop offset="60%" stopColor={COLORS.navy} stopOpacity="1" />
        <stop offset="100%" stopColor={COLORS.navyDeep} stopOpacity="1" />
      </radialGradient>
      <linearGradient id="goldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={COLORS.goldLight} />
        <stop offset="60%" stopColor={COLORS.gold} />
        <stop offset="100%" stopColor={COLORS.goldDeep} />
      </linearGradient>
      <radialGradient id="goldGlow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor={COLORS.gold} stopOpacity="0.55" />
        <stop offset="60%" stopColor={COLORS.gold} stopOpacity="0.06" />
        <stop offset="100%" stopColor={COLORS.gold} stopOpacity="0" />
      </radialGradient>
      <radialGradient id="blueGlow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor={COLORS.blueSoft} stopOpacity="0.45" />
        <stop offset="100%" stopColor={COLORS.blueSoft} stopOpacity="0" />
      </radialGradient>
      <filter id="softGlow" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="2" result="b" />
        <feMerge>
          <feMergeNode in="b" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
  );
}

function BackgroundLayer() {
  return (
    <>
      <rect width="100%" height="100%" fill="url(#bgGrad)" />
      {/* subtle grid */}
      <g opacity="0.15">
        {Array.from({ length: 24 }).map((_, i) => (
          <line key={`v${i}`} x1={i * 25} y1="0" x2={i * 25} y2="600" stroke={COLORS.gold} strokeWidth="0.3" />
        ))}
        {Array.from({ length: 24 }).map((_, i) => (
          <line key={`h${i}`} x1="0" y1={i * 25} x2="600" y2={i * 25} stroke={COLORS.gold} strokeWidth="0.3" />
        ))}
      </g>
      {/* corner glows */}
      <circle cx="100" cy="100" r="180" fill="url(#goldGlow)" />
      <circle cx="500" cy="500" r="220" fill="url(#blueGlow)" />
    </>
  );
}

/* --------------------------------------------------------------------- */
/* HERO — Global intelligence network globe                              */
/* --------------------------------------------------------------------- */
export function HeroVisual({ className = '' }) {
  return (
    <svg viewBox="0 0 600 600" className={className} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <Defs />
      <BackgroundLayer />

      {/* orbital rings */}
      <g transform="translate(300 300)">
        <ellipse rx="240" ry="80" fill="none" stroke={COLORS.gold} strokeOpacity="0.35" strokeWidth="0.8" />
        <ellipse rx="240" ry="80" fill="none" stroke={COLORS.gold} strokeOpacity="0.25" strokeWidth="0.8" transform="rotate(35)" />
        <ellipse rx="240" ry="80" fill="none" stroke={COLORS.gold} strokeOpacity="0.2" strokeWidth="0.8" transform="rotate(-35)" />
      </g>

      {/* globe sphere */}
      <g transform="translate(300 300)" filter="url(#softGlow)">
        <circle r="135" fill={COLORS.navyMid} fillOpacity="0.85" stroke={COLORS.gold} strokeOpacity="0.6" strokeWidth="1.2" />
        {/* meridians */}
        {[0, 30, 60, 90, 120, 150].map((deg) => (
          <ellipse key={deg} rx="135" ry={Math.max(20, 135 * Math.abs(Math.cos((deg * Math.PI) / 180)))}
            fill="none" stroke={COLORS.gold} strokeOpacity="0.28" strokeWidth="0.7" transform={`rotate(${deg})`} />
        ))}
        {/* latitudes */}
        {[-80, -50, -20, 20, 50, 80].map((y) => {
          const r = Math.sqrt(135 * 135 - y * y);
          return <line key={y} x1={-r} y1={y} x2={r} y2={y} stroke={COLORS.gold} strokeOpacity="0.22" strokeWidth="0.6" />;
        })}
        {/* city nodes */}
        {[
          [-90, -40], [-30, -70], [40, -30], [80, 20], [-50, 50], [10, 70], [-20, 10],
        ].map(([x, y], i) => (
          <g key={i}>
            <circle cx={x} cy={y} r="3.5" fill={COLORS.goldLight} />
            <circle cx={x} cy={y} r="9" fill="none" stroke={COLORS.gold} strokeOpacity="0.5" />
          </g>
        ))}
      </g>

      {/* floating data tiles */}
      <g opacity="0.9">
        <rect x="60" y="80" width="120" height="70" rx="10" fill={COLORS.navy} stroke={COLORS.gold} strokeOpacity="0.45" />
        <rect x="78" y="98" width="50" height="4" rx="2" fill={COLORS.gold} />
        <rect x="78" y="108" width="80" height="2" rx="1" fill={COLORS.blueSoft} opacity="0.7" />
        <rect x="78" y="115" width="68" height="2" rx="1" fill={COLORS.blueSoft} opacity="0.5" />
        <rect x="78" y="125" width="40" height="14" rx="3" fill="none" stroke={COLORS.gold} strokeOpacity="0.6" />

        <rect x="430" y="430" width="140" height="80" rx="10" fill={COLORS.navy} stroke={COLORS.gold} strokeOpacity="0.45" />
        <polyline points="450,490 470,470 490,485 510,460 530,475 555,455" fill="none" stroke={COLORS.gold} strokeWidth="1.5" />
        <circle cx="555" cy="455" r="3" fill={COLORS.goldLight} />

        <rect x="60" y="430" width="110" height="60" rx="10" fill={COLORS.navy} stroke={COLORS.gold} strokeOpacity="0.4" />
        <circle cx="90" cy="460" r="14" fill="none" stroke={COLORS.gold} strokeOpacity="0.6" strokeWidth="2" />
        <path d="M 76 460 a 14 14 0 0 1 14 -14" fill="none" stroke={COLORS.goldLight} strokeWidth="2" />
        <rect x="115" y="450" width="45" height="3" rx="1.5" fill={COLORS.gold} />
        <rect x="115" y="460" width="40" height="2" rx="1" fill={COLORS.blueSoft} />
        <rect x="115" y="468" width="30" height="2" rx="1" fill={COLORS.blueSoft} opacity="0.6" />
      </g>

      {/* particles */}
      {Array.from({ length: 36 }).map((_, i) => {
        const x = (i * 47) % 600;
        const y = (i * 89) % 600;
        const r = (i % 3) * 0.6 + 0.6;
        return <circle key={i} cx={x} cy={y} r={r} fill={COLORS.goldLight} opacity={0.3 + (i % 4) * 0.15} />;
      })}
    </svg>
  );
}

/* --------------------------------------------------------------------- */
/* ABOUT — Architectural pavilion (governance / integrity)               */
/* --------------------------------------------------------------------- */
export function AboutVisual({ className = '' }) {
  return (
    <svg viewBox="0 0 480 600" className={className} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <Defs />
      <rect width="100%" height="100%" fill="url(#bgGrad)" />
      <g opacity="0.12">
        {Array.from({ length: 20 }).map((_, i) => (
          <line key={`v${i}`} x1={i * 25} y1="0" x2={i * 25} y2="600" stroke={COLORS.gold} strokeWidth="0.3" />
        ))}
      </g>
      <circle cx="240" cy="320" r="220" fill="url(#goldGlow)" opacity="0.7" />

      {/* pediment */}
      <g transform="translate(240 200)">
        <polygon points="-150,0 150,0 0,-90" fill={COLORS.navyMid} stroke="url(#goldGrad)" strokeWidth="2" />
        <polygon points="-130,-5 130,-5 0,-75" fill="none" stroke={COLORS.gold} strokeOpacity="0.35" />
        <circle r="10" cy="-30" fill="url(#goldGrad)" />
      </g>

      {/* entablature */}
      <rect x="80" y="200" width="320" height="22" fill={COLORS.navyMid} stroke="url(#goldGrad)" strokeWidth="1.4" />
      <line x1="80" y1="222" x2="400" y2="222" stroke={COLORS.gold} strokeOpacity="0.4" />

      {/* columns */}
      {[110, 180, 250, 320, 380].map((cx, i) => (
        <g key={cx}>
          <rect x={cx - 14} y="222" width="28" height="220" fill={COLORS.navyMid} stroke="url(#goldGrad)" strokeWidth="1.1" />
          {/* fluting */}
          {[-8, -4, 0, 4, 8].map((dx) => (
            <line key={dx} x1={cx + dx} y1="226" x2={cx + dx} y2="438" stroke={COLORS.gold} strokeOpacity="0.18" />
          ))}
          {/* capital */}
          <rect x={cx - 18} y="218" width="36" height="6" fill="url(#goldGrad)" />
          {/* base */}
          <rect x={cx - 18} y="442" width="36" height="6" fill="url(#goldGrad)" />
        </g>
      ))}

      {/* stylobate */}
      <rect x="60" y="448" width="360" height="14" fill={COLORS.navyMid} stroke="url(#goldGrad)" strokeWidth="1.2" />
      <rect x="50" y="462" width="380" height="10" fill={COLORS.navy} stroke={COLORS.gold} strokeOpacity="0.35" />

      {/* data filaments */}
      <g opacity="0.55">
        <path d="M 0 540 Q 240 470 480 540" fill="none" stroke={COLORS.gold} strokeWidth="0.8" />
        <path d="M 0 560 Q 240 500 480 560" fill="none" stroke={COLORS.blueSoft} strokeOpacity="0.6" />
        <path d="M 0 580 Q 240 530 480 580" fill="none" stroke={COLORS.gold} strokeOpacity="0.4" />
      </g>

      {/* floating spec dots */}
      {Array.from({ length: 24 }).map((_, i) => (
        <circle key={i} cx={(i * 39) % 480} cy={(i * 71) % 200} r={(i % 3) * 0.5 + 0.6} fill={COLORS.goldLight} opacity={0.25 + (i % 4) * 0.15} />
      ))}
    </svg>
  );
}

/* --------------------------------------------------------------------- */
/* SERVICES — Floating dashboard panels                                   */
/* --------------------------------------------------------------------- */
export function ServicesVisual({ className = '' }) {
  return (
    <svg viewBox="0 0 800 460" className={className} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <Defs />
      <rect width="100%" height="100%" fill="url(#bgGrad)" />
      <circle cx="400" cy="230" r="280" fill="url(#goldGlow)" opacity="0.6" />

      {/* central panel - finance chart */}
      <g transform="translate(280 110)">
        <rect width="240" height="240" rx="14" fill={COLORS.navy} stroke="url(#goldGrad)" strokeWidth="1.6" />
        <text x="20" y="34" fill={COLORS.gold} fontFamily="serif" fontSize="13" letterSpacing="3">STRATEGY</text>
        <line x1="20" y1="46" x2="220" y2="46" stroke={COLORS.gold} strokeOpacity="0.3" />
        <polyline points="30,180 60,150 90,165 120,120 150,135 180,90 210,100" fill="none" stroke="url(#goldGrad)" strokeWidth="2" />
        {[[30, 180], [60, 150], [90, 165], [120, 120], [150, 135], [180, 90], [210, 100]].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="3" fill={COLORS.goldLight} />
        ))}
        <rect x="20" y="205" width="200" height="3" rx="1.5" fill={COLORS.blueSoft} opacity="0.6" />
        <rect x="20" y="214" width="140" height="3" rx="1.5" fill={COLORS.blueSoft} opacity="0.4" />
      </g>

      {/* left panel - compliance */}
      <g transform="translate(50 60)">
        <rect width="180" height="150" rx="12" fill={COLORS.navy} stroke={COLORS.gold} strokeOpacity="0.55" />
        <path d="M 90 30 L 110 40 L 110 70 Q 110 95 90 110 Q 70 95 70 70 L 70 40 Z" fill="none" stroke="url(#goldGrad)" strokeWidth="1.6" />
        <path d="M 80 70 L 88 78 L 102 64" fill="none" stroke={COLORS.goldLight} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="30" y="120" width="120" height="3" rx="1.5" fill={COLORS.gold} opacity="0.7" />
        <rect x="30" y="130" width="90" height="2" rx="1" fill={COLORS.blueSoft} opacity="0.5" />
      </g>

      {/* left lower - tech node */}
      <g transform="translate(50 240)">
        <rect width="180" height="160" rx="12" fill={COLORS.navy} stroke={COLORS.gold} strokeOpacity="0.5" />
        <g transform="translate(90 80)" stroke="url(#goldGrad)" fill="none" strokeWidth="1.2">
          <circle r="8" />
          {[0, 60, 120, 180, 240, 300].map((deg) => {
            const x = Math.cos((deg * Math.PI) / 180) * 45;
            const y = Math.sin((deg * Math.PI) / 180) * 45;
            return (
              <g key={deg}>
                <line x1="0" y1="0" x2={x} y2={y} strokeOpacity="0.55" />
                <circle cx={x} cy={y} r="4" fill={COLORS.gold} />
              </g>
            );
          })}
        </g>
      </g>

      {/* right panel - audit */}
      <g transform="translate(570 60)">
        <rect width="180" height="150" rx="12" fill={COLORS.navy} stroke={COLORS.gold} strokeOpacity="0.55" />
        {[40, 60, 80, 100, 120, 140].map((x, i) => {
          const h = 30 + ((i * 17) % 60);
          return <rect key={x} x={x} y={110 - h} width="14" height={h} rx="2" fill={i % 2 === 0 ? 'url(#goldGrad)' : COLORS.blueSoft} opacity={i % 2 === 0 ? 1 : 0.55} />;
        })}
        <line x1="30" y1="115" x2="160" y2="115" stroke={COLORS.gold} strokeOpacity="0.45" />
      </g>

      {/* right lower - capital */}
      <g transform="translate(570 240)">
        <rect width="180" height="160" rx="12" fill={COLORS.navy} stroke={COLORS.gold} strokeOpacity="0.5" />
        <text x="90" y="80" fill="url(#goldGrad)" fontFamily="serif" fontSize="44" fontWeight="500" textAnchor="middle">$</text>
        <circle cx="90" cy="75" r="38" fill="none" stroke={COLORS.gold} strokeOpacity="0.5" />
        <circle cx="90" cy="75" r="52" fill="none" stroke={COLORS.gold} strokeOpacity="0.3" />
        <rect x="30" y="130" width="120" height="3" rx="1.5" fill={COLORS.gold} opacity="0.7" />
        <rect x="30" y="140" width="80" height="2" rx="1" fill={COLORS.blueSoft} opacity="0.5" />
      </g>

      {/* connecting lines */}
      <g opacity="0.45" stroke="url(#goldGrad)" strokeWidth="0.8" fill="none">
        <path d="M 230 135 Q 280 200 280 230" />
        <path d="M 230 320 Q 280 280 280 230" />
        <path d="M 570 135 Q 520 200 520 230" />
        <path d="M 570 320 Q 520 280 520 230" />
      </g>

      {/* particles */}
      {Array.from({ length: 24 }).map((_, i) => (
        <circle key={i} cx={(i * 67) % 800} cy={(i * 41) % 460} r={(i % 3) * 0.5 + 0.6} fill={COLORS.goldLight} opacity={0.25 + (i % 4) * 0.12} />
      ))}
    </svg>
  );
}

/* --------------------------------------------------------------------- */
/* INDUSTRIES — Hexagonal sector network                                  */
/* --------------------------------------------------------------------- */
export function IndustriesVisual({ className = '' }) {
  const hexPath = (cx, cy, r) => {
    const pts = [];
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i - Math.PI / 6;
      pts.push(`${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`);
    }
    return pts.join(' ');
  };

  const hexes = [
    [200, 160], [320, 130], [440, 160], [560, 130],
    [200, 280], [320, 250], [440, 280], [560, 250],
    [260, 380], [380, 350], [500, 380],
  ];

  return (
    <svg viewBox="0 0 760 480" className={className} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <Defs />
      <rect width="100%" height="100%" fill="url(#bgGrad)" />
      <circle cx="380" cy="240" r="320" fill="url(#goldGlow)" opacity="0.5" />

      {/* faint world map suggestion (continent silhouettes) */}
      <g opacity="0.13" fill={COLORS.gold}>
        <ellipse cx="180" cy="240" rx="80" ry="55" />
        <ellipse cx="380" cy="220" rx="110" ry="70" />
        <ellipse cx="580" cy="260" rx="90" ry="60" />
        <ellipse cx="300" cy="370" rx="60" ry="35" />
        <ellipse cx="550" cy="380" rx="70" ry="40" />
      </g>

      {/* connections */}
      <g opacity="0.5" stroke="url(#goldGrad)" strokeWidth="0.9" fill="none">
        {hexes.map(([x, y], i) =>
          hexes.slice(i + 1).map(([x2, y2], j) => {
            const d = Math.hypot(x2 - x, y2 - y);
            if (d > 160) return null;
            return <line key={`${i}-${j}`} x1={x} y1={y} x2={x2} y2={y2} strokeOpacity={0.35} />;
          })
        )}
      </g>

      {/* hex nodes */}
      {hexes.map(([cx, cy], i) => (
        <g key={i}>
          <polygon points={hexPath(cx, cy, 38)} fill={COLORS.navyMid} stroke="url(#goldGrad)" strokeWidth="1.4" />
          <polygon points={hexPath(cx, cy, 30)} fill="none" stroke={COLORS.gold} strokeOpacity="0.3" />
          <circle cx={cx} cy={cy} r="6" fill={COLORS.goldLight} />
          <circle cx={cx} cy={cy} r="14" fill="none" stroke={COLORS.gold} strokeOpacity="0.45" />
        </g>
      ))}

      {/* particles */}
      {Array.from({ length: 30 }).map((_, i) => (
        <circle key={i} cx={(i * 53) % 760} cy={(i * 91) % 480} r={(i % 3) * 0.5 + 0.6} fill={COLORS.goldLight} opacity={0.22 + (i % 4) * 0.12} />
      ))}
    </svg>
  );
}

/* --------------------------------------------------------------------- */
/* CLIENTS — Orbital constellation of nodes                              */
/* --------------------------------------------------------------------- */
export function ClientsVisual({ className = '' }) {
  return (
    <svg viewBox="0 0 760 480" className={className} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <Defs />
      <rect width="100%" height="100%" fill="url(#bgGrad)" />
      <circle cx="380" cy="240" r="320" fill="url(#goldGlow)" opacity="0.6" />

      {/* central emblem */}
      <g transform="translate(380 240)">
        <circle r="58" fill={COLORS.navyMid} stroke="url(#goldGrad)" strokeWidth="2" />
        <circle r="44" fill="none" stroke={COLORS.gold} strokeOpacity="0.45" />
        <polygon points="0,-26 22,-8 14,18 -14,18 -22,-8" fill="url(#goldGrad)" opacity="0.85" />
      </g>

      {/* three orbits with nodes */}
      {[
        { rx: 160, ry: 80, rot: 0, count: 6 },
        { rx: 230, ry: 110, rot: 25, count: 8 },
        { rx: 300, ry: 145, rot: -15, count: 10 },
      ].map((orbit, idx) => (
        <g key={idx} transform={`translate(380 240) rotate(${orbit.rot})`}>
          <ellipse rx={orbit.rx} ry={orbit.ry} fill="none" stroke={COLORS.gold} strokeOpacity={0.35 - idx * 0.07} strokeWidth="0.9" />
          {Array.from({ length: orbit.count }).map((_, i) => {
            const angle = (i / orbit.count) * Math.PI * 2;
            const x = Math.cos(angle) * orbit.rx;
            const y = Math.sin(angle) * orbit.ry;
            return (
              <g key={i}>
                <circle cx={x} cy={y} r="14" fill={COLORS.navyMid} stroke="url(#goldGrad)" strokeWidth="1.2" />
                <circle cx={x} cy={y} r="5" fill={COLORS.goldLight} />
              </g>
            );
          })}
        </g>
      ))}

      {/* faint connection beams */}
      <g opacity="0.35" stroke="url(#goldGrad)" strokeWidth="0.6">
        {Array.from({ length: 14 }).map((_, i) => {
          const a = (i / 14) * Math.PI * 2;
          const x1 = 380 + Math.cos(a) * 60;
          const y1 = 240 + Math.sin(a) * 60;
          const x2 = 380 + Math.cos(a) * 320;
          const y2 = 240 + Math.sin(a) * 320;
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
        })}
      </g>

      {/* particles */}
      {Array.from({ length: 28 }).map((_, i) => (
        <circle key={i} cx={(i * 47) % 760} cy={(i * 83) % 480} r={(i % 3) * 0.5 + 0.5} fill={COLORS.goldLight} opacity={0.2 + (i % 4) * 0.13} />
      ))}
    </svg>
  );
}

/* --------------------------------------------------------------------- */
/* CONTACT — Connection pulses                                            */
/* --------------------------------------------------------------------- */
export function ContactVisual({ className = '' }) {
  return (
    <svg viewBox="0 0 760 380" className={className} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <Defs />
      <rect width="100%" height="100%" fill="url(#bgGrad)" />
      <circle cx="200" cy="190" r="180" fill="url(#goldGlow)" opacity="0.55" />
      <circle cx="560" cy="190" r="180" fill="url(#blueGlow)" opacity="0.7" />

      {/* skyline silhouette */}
      <g opacity="0.18" fill={COLORS.gold}>
        <rect x="30" y="300" width="20" height="60" />
        <rect x="55" y="280" width="14" height="80" />
        <rect x="72" y="290" width="18" height="70" />
        <rect x="95" y="260" width="22" height="100" />
        <rect x="122" y="285" width="14" height="75" />
        <rect x="640" y="290" width="20" height="70" />
        <rect x="665" y="275" width="16" height="85" />
        <rect x="685" y="295" width="14" height="65" />
        <rect x="703" y="270" width="20" height="90" />
        <rect x="728" y="290" width="14" height="70" />
      </g>

      {/* two pulses */}
      <g>
        <circle cx="200" cy="190" r="38" fill="url(#goldGrad)" opacity="0.9" />
        <circle cx="200" cy="190" r="55" fill="none" stroke={COLORS.gold} strokeOpacity="0.5" />
        <circle cx="200" cy="190" r="80" fill="none" stroke={COLORS.gold} strokeOpacity="0.3" />
        <circle cx="560" cy="190" r="38" fill={COLORS.navyMid} stroke="url(#goldGrad)" strokeWidth="2.5" />
        <circle cx="560" cy="190" r="55" fill="none" stroke={COLORS.gold} strokeOpacity="0.45" />
        <circle cx="560" cy="190" r="80" fill="none" stroke={COLORS.gold} strokeOpacity="0.25" />
      </g>

      {/* connection corridor */}
      <g>
        <path d="M 240 190 Q 380 110 520 190" fill="none" stroke="url(#goldGrad)" strokeWidth="1.6" />
        <path d="M 240 190 Q 380 270 520 190" fill="none" stroke="url(#goldGrad)" strokeWidth="1.6" />
        {[280, 340, 380, 420, 480].map((x, i) => (
          <circle key={x} cx={x} cy={i % 2 === 0 ? 150 : 230} r="3" fill={COLORS.goldLight} />
        ))}
      </g>

      {/* particles */}
      {Array.from({ length: 26 }).map((_, i) => (
        <circle key={i} cx={(i * 59) % 760} cy={(i * 71) % 380} r={(i % 3) * 0.5 + 0.5} fill={COLORS.goldLight} opacity={0.22 + (i % 4) * 0.13} />
      ))}
    </svg>
  );
}
